import { useQuery } from "@tanstack/react-query";
import { fetchBillingInputs } from "@/lib/billing-query";
import {
  calcBillingRatesData,
  type EmployeeBillingData,
} from "@/lib/billing-rates-calc";

/**
 * Per-user variant of {@link useBillingRates} for profile pages (MaalTab).
 *
 * Mirrors the queries in `useBillingRates` but filters every Supabase fetch on
 * `user_id = userId`, so we avoid pulling the full company dataset just to
 * render one employee's KPI card. Returns the single {@link EmployeeBillingData}
 * (or `null` if the user row is missing).
 *
 * `useBillingRates` is intentionally left untouched for dashboards and team
 * overviews; this hook is for single-user views only.
 */
export function useEmployeeBillingRate(
  userId: string | null | undefined,
  year: number,
) {
  return useQuery({
    queryKey: ["employee-billing-rate", userId, year],
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<EmployeeBillingData | null> => {
      const inputs = await fetchBillingInputs({ year, userId: userId! });
      if (inputs.users.length === 0) return null;

      const result = calcBillingRatesData({ ...inputs, year, now: new Date() });
      return result.employees[0] ?? null;
    },
  });
}
